'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { X, Bookmark, Bell, Share2, MessageSquare, DollarSign, Lightbulb, TrendingUp, Edit2, Heart, Check, AlertCircle, MapPin, Trash2 } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import LikeButton from '@/components/ui/LikeButton';
import { createClient } from '@/lib/supabase/client';

import SolutionCard from './SolutionCard';
import FundingOfferForm from './FundingOfferForm';
import CommentSection from './CommentSection';
import type { Problem, Comment, Solution } from './mockData';

type DetailTab = 'overview' | 'solutions' | 'comments' | 'funding';

const CONTINENTS = [
  'Africa', 'Antarctica', 'Asia', 'Europe',
  'North America', 'Oceania', 'South America',
];

function wordCount(text: string): number {
  return text.trim() === '' ? 0 : text.trim().split(/\s+/).length;
}

interface EditFormData {
  title: string;
  excerpt: string;
  fullDescription: string;
  affectedGroup: string;
  effects: string;
  workaround: string;
  proposedSolution: string;
  town: string;
  country: string;
  continent: string;
}

interface DetailPanelProps {
  problem: Problem;
  currentUserId: string | null;
  onClose: () => void;
  onUpdateProblem: (updated: Problem) => void;
  onDeleteProblem: (problemId: string) => void;
  onUpdateSolution: (problemId: string, updated: Solution) => void;
  onDeleteSolution: (problemId: string, solutionId: string) => void;
  onProposeSolution?: () => void;
}

const IS_UUID = (id: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

export default function DetailPanel({
  problem,
  currentUserId,
  onClose,
  onUpdateProblem,
  onDeleteProblem,
  onUpdateSolution,
  onDeleteSolution,
  onProposeSolution,
}: DetailPanelProps) {
  const [activeTab, setActiveTab] = useState<DetailTab>('overview');
  const [bookmarked, setBookmarked] = useState(false);
  const [following, setFollowing] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(problem.bookmarks);
  const [showFundingForm, setShowFundingForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editErrors, setEditErrors] = useState<Partial<Record<keyof EditFormData, string>>>({});
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const router = useRouter();

  // Ownership: DB problem → compare user_id; local mock → compare poster === 'You'
  const isPoster = IS_UUID(problem.id)
    ? !!(currentUserId && problem.userId === currentUserId)
    : problem.poster === 'You';

  // Parse location from fullDescription if available
  function parseLocation(desc: string) {
    const match = desc.match(/Location:\s*([^,]+),\s*([^,]+),\s*(.+)/);
    if (match) return { town: match[1].trim(), country: match[2].trim(), continent: match[3].trim() };
    return { town: '', country: '', continent: '' };
  }

  const loc = parseLocation(problem.fullDescription);

  const [editForm, setEditForm] = useState<EditFormData>({
    title: problem.title,
    excerpt: problem.excerpt,
    fullDescription: problem.fullDescription,
    affectedGroup: problem.affectedGroup || '',
    effects: problem.effects || '',
    workaround: problem.workaround || '',
    proposedSolution: problem.proposedSolution || '',
    town: problem.town || loc.town,
    country: problem.country || loc.country,
    continent: problem.continent || loc.continent,
  });

  const fundingPct =
    problem.fundingTarget > 0
      ? Math.round((problem.fundingRaised / problem.fundingTarget) * 100)
      : 0;

  const TABS: { id: DetailTab; label: string; count?: number }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'solutions', label: 'Solutions', count: problem.solutions.length },
    { id: 'comments', label: 'Comments', count: problem.comments.length },
    { id: 'funding', label: 'Funding Offers', count: problem.solutions.reduce((a, s) => a + s.fundingOffers.length, 0) },
  ];

  function handleLike() {
    if (liked) {
      setLiked(false);
      setLikeCount((c) => c - 1);
    } else {
      setLiked(true);
      setLikeCount((c) => c + 1);
    }
  }

  function handleEditChange(field: keyof EditFormData, value: string) {
    setEditForm((prev) => ({ ...prev, [field]: value }));
    if (editErrors[field]) setEditErrors((prev) => ({ ...prev, [field]: '' }));
  }

  function validateEdit(): boolean {
    const errs: Partial<Record<keyof EditFormData, string>> = {};
    if (!editForm.title.trim()) errs.title = 'Required.';
    if (!editForm.excerpt.trim()) errs.excerpt = 'Required.';
    else if (wordCount(editForm.excerpt) > 50) errs.excerpt = 'Max 50 words.';
    setEditErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleEditSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validateEdit()) return;

    const locationLine = editForm.town && editForm.country && editForm.continent
      ? `\n\nLocation: ${editForm.town}, ${editForm.country}, ${editForm.continent}`
      : '';

    const updatedProblem: Problem = {
      ...problem,
      title: editForm.title,
      excerpt: editForm.excerpt,
      fullDescription: editForm.fullDescription + locationLine,
      affectedGroup: editForm.affectedGroup,
      effects: editForm.effects,
      workaround: editForm.workaround,
      proposedSolution: editForm.proposedSolution,
      town: editForm.town,
      country: editForm.country,
      continent: editForm.continent,
    };

    // Persist to DB if it's a real DB problem
    if (IS_UUID(problem.id)) {
      setSaving(true);
      try {
        const supabase = createClient();
        await supabase.from('problems').update({
          title: editForm.title,
          excerpt: editForm.excerpt,
          full_description: editForm.fullDescription + locationLine,
          affected_group: editForm.affectedGroup,
          effects: editForm.effects,
          workaround: editForm.workaround,
          proposed_solution: editForm.proposedSolution,
          town: editForm.town,
          country: editForm.country,
          continent: editForm.continent,
          updated_at: new Date().toISOString(),
        }).eq('id', problem.id);
      } finally {
        setSaving(false);
      }
    }

    onUpdateProblem(updatedProblem);
    setShowEditForm(false);
  }

  async function handleDeleteProblem() {
    if (IS_UUID(problem.id)) {
      setDeleting(true);
      try {
        const supabase = createClient();
        await supabase.from('problems').delete().eq('id', problem.id);
      } finally {
        setDeleting(false);
      }
    }
    onDeleteProblem(problem.id);
  }

  function handleCommentsUpdate(updatedComments: Comment[]) {
    onUpdateProblem({ ...problem, comments: updatedComments });
  }

  const excerptWords = wordCount(editForm.excerpt);

  return (
    <div className="w-[420px] xl:w-[480px] 2xl:w-[520px] shrink-0 flex flex-col bg-card border-l border-border overflow-hidden animate-slide-in-right hidden md:flex"
      style={{ minHeight: 'calc(100vh - 64px)' }}
    >
      {/* Header */}
      <div className="p-5 border-b border-border shrink-0">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${problem.categoryColor}`}>
              {problem.category}
            </span>
            <StatusBadge variant={problem.status} />
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {isPoster && (
              <>
                <button
                  onClick={() => setShowEditForm((v) => !v)}
                  className={`p-1.5 rounded-lg transition-colors ${showEditForm ? 'bg-primary/10 text-primary' : 'hover:bg-muted text-muted-foreground hover:text-foreground'}`}
                  aria-label="Edit problem"
                  title="Edit your problem"
                >
                  <Edit2 size={15} />
                </button>
                <button
                  onClick={() => setDeleteConfirm(true)}
                  className="p-1.5 rounded-lg hover:bg-danger/10 text-muted-foreground hover:text-danger transition-colors"
                  aria-label="Delete problem"
                  title="Delete your problem"
                >
                  <Trash2 size={15} />
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-muted transition-colors"
              aria-label="Close detail panel"
            >
              <X size={16} className="text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Delete Confirmation */}
        {deleteConfirm && isPoster && (
          <div className="mb-4 bg-danger/5 border border-danger/20 rounded-xl p-4 space-y-3">
            <p className="text-xs font-bold text-danger flex items-center gap-1.5">
              <Trash2 size={12} /> Delete this problem?
            </p>
            <p className="text-xs text-muted-foreground">This action cannot be undone. All associated data will be removed.</p>
            <div className="flex gap-2">
              <button
                onClick={handleDeleteProblem}
                disabled={deleting}
                className="flex items-center gap-1.5 px-4 py-2 bg-danger text-white text-xs font-bold rounded-lg hover:bg-red-600 active:scale-95 transition-all disabled:opacity-60"
              >
                {deleting ? 'Deleting…' : <><Trash2 size={12} /> Yes, Delete</>}
              </button>
              <button
                onClick={() => setDeleteConfirm(false)}
                className="px-4 py-2 bg-muted text-foreground text-xs font-semibold rounded-lg hover:bg-muted/80 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Edit Form (inline, only for poster) */}
        {showEditForm && isPoster && (
          <form onSubmit={handleEditSubmit} className="mb-4 bg-muted/40 border border-border rounded-xl p-4 space-y-3">
            <p className="text-xs font-bold text-foreground uppercase tracking-wide flex items-center gap-1.5">
              <Edit2 size={12} className="text-primary" /> Edit Your Problem
            </p>

            {/* Title */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1">Title <span className="text-danger">*</span></label>
              <input
                type="text"
                value={editForm.title}
                onChange={(e) => handleEditChange('title', e.target.value)}
                className={`w-full px-3 py-2 rounded-lg border text-xs text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary transition-all ${editErrors.title ? 'border-danger' : 'border-border'}`}
              />
              {editErrors.title && <p className="text-xs text-danger mt-0.5">{editErrors.title}</p>}
            </div>

            {/* Excerpt / Problem summary */}
            <div>
              <label className="flex items-center gap-1 text-xs font-semibold text-muted-foreground mb-1">
                <AlertCircle size={10} className="text-warning" /> Problem Summary (max 50 words) <span className="text-danger">*</span>
              </label>
              <textarea
                value={editForm.excerpt}
                onChange={(e) => handleEditChange('excerpt', e.target.value)}
                rows={3}
                className={`w-full px-3 py-2 rounded-lg border text-xs text-foreground bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary transition-all ${editErrors.excerpt ? 'border-danger' : 'border-border'}`}
              />
              <div className="flex justify-between mt-0.5">
                {editErrors.excerpt ? <p className="text-xs text-danger">{editErrors.excerpt}</p> : <span />}
                <span className={`text-xs tabular-nums ${excerptWords > 50 ? 'text-danger' : 'text-muted-foreground'}`}>{excerptWords}/50</span>
              </div>
            </div>

            {/* Full Description */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1">Full Description</label>
              <textarea
                value={editForm.fullDescription}
                onChange={(e) => handleEditChange('fullDescription', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 rounded-lg border border-border text-xs text-foreground bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary transition-all"
              />
            </div>

            {/* Affected Group */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1">Affected Group</label>
              <textarea
                value={editForm.affectedGroup}
                onChange={(e) => handleEditChange('affectedGroup', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 rounded-lg border border-border text-xs text-foreground bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary transition-all"
              />
            </div>

            {/* Effects */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1">Effects</label>
              <textarea
                value={editForm.effects}
                onChange={(e) => handleEditChange('effects', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 rounded-lg border border-border text-xs text-foreground bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary transition-all"
              />
            </div>

            {/* Workaround */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1">Current Workaround</label>
              <textarea
                value={editForm.workaround}
                onChange={(e) => handleEditChange('workaround', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 rounded-lg border border-border text-xs text-foreground bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary transition-all"
              />
            </div>

            {/* Proposed Solution */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1">Proposed Solution</label>
              <textarea
                value={editForm.proposedSolution}
                onChange={(e) => handleEditChange('proposedSolution', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 rounded-lg border border-border text-xs text-foreground bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary transition-all"
              />
            </div>

            {/* Location */}
            <div>
              <label className="flex items-center gap-1 text-xs font-semibold text-muted-foreground mb-1">
                <MapPin size={10} className="text-primary" /> Location
              </label>
              <div className="grid grid-cols-3 gap-2">
                <input
                  type="text"
                  value={editForm.town}
                  onChange={(e) => handleEditChange('town', e.target.value)}
                  placeholder="Town"
                  className="w-full px-2.5 py-1.5 rounded-lg border border-border text-xs text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                />
                <input
                  type="text"
                  value={editForm.country}
                  onChange={(e) => handleEditChange('country', e.target.value)}
                  placeholder="Country"
                  className="w-full px-2.5 py-1.5 rounded-lg border border-border text-xs text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                />
                <select
                  value={editForm.continent}
                  onChange={(e) => handleEditChange('continent', e.target.value)}
                  className="w-full px-2.5 py-1.5 rounded-lg border border-border text-xs text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                >
                  <option value="">Continent</option>
                  {CONTINENTS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div className="flex gap-2 pt-1">
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground text-xs font-bold rounded-lg hover:bg-cyan-600 active:scale-95 transition-all disabled:opacity-60"
              >
                <Check size={12} /> {saving ? 'Saving…' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={() => setShowEditForm(false)}
                className="px-4 py-2 bg-muted text-foreground text-xs font-semibold rounded-lg hover:bg-muted/80 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        <h2 className="text-base font-bold text-foreground leading-snug mb-3">{problem.title}</h2>

        {/* Poster info */}
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
            <span className="text-xs font-bold text-orange-600">{problem.poster.charAt(0)}</span>
          </div>
          <div>
            <p className="text-xs font-semibold text-foreground leading-none">{problem.poster}</p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <StatusBadge variant="poster" size="sm" />
              <span className="text-xs text-success font-semibold">⭐ {problem.posterReputation}</span>
            </div>
          </div>
          <span className="ml-auto text-xs text-muted-foreground">{problem.postedAt}</span>
        </div>

        {/* Funding progress */}
        <div className="bg-muted/50 rounded-xl p-3 mb-4">
          <div className="flex items-center justify-between text-xs mb-2">
            <span className="font-semibold text-foreground tabular-nums">
              ${(problem.fundingRaised / 1000).toFixed(0)}K raised
            </span>
            <span className="text-muted-foreground tabular-nums">
              Goal: ${(problem.fundingTarget / 1000).toFixed(0)}K
            </span>
          </div>
          <div className="h-2 bg-border rounded-full overflow-hidden mb-1.5">
            <div
              className={`h-full rounded-full transition-all duration-500 ${fundingPct >= 100 ? 'bg-success' : 'bg-primary'}`}
              style={{ width: `${Math.min(fundingPct, 100)}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className={`font-semibold ${fundingPct >= 100 ? 'text-success' : 'text-primary'}`}>
              {fundingPct}% funded
            </span>
            <span className="text-muted-foreground">{problem.solutions.length} proposals · {problem.followers} followers</span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setBookmarked(!bookmarked)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition-all duration-150 active:scale-95 ${
              bookmarked
                ? 'bg-primary/10 border-primary/30 text-primary' :'bg-card border-border text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            <Bookmark size={13} className={bookmarked ? 'fill-primary' : ''} />
            {bookmarked ? 'Saved' : 'Save'}
          </button>
          <button
            onClick={() => setFollowing(!following)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition-all duration-150 active:scale-95 ${
              following
                ? 'bg-success/10 border-success/30 text-success' :'bg-card border-border text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            <Bell size={13} />
            {following ? 'Following' : 'Follow'}
          </button>
          {/* Like button — use Supabase LikeButton for DB problems, local for mock */}
          {IS_UUID(problem.id) ? (
            <LikeButton
              targetType="problem"
              targetId={problem.id}
              initialCount={problem.bookmarks}
              size={13}
              showCount={true}
              className="border border-border"
            />
          ) : (
            !isPoster && (
              <button
                onClick={handleLike}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition-all duration-150 active:scale-95 ${
                  liked
                    ? 'bg-rose-50 border-rose-300 text-rose-500' :'bg-card border-border text-muted-foreground hover:bg-rose-50 hover:border-rose-200 hover:text-rose-500'
                }`}
              >
                <Heart size={13} className={liked ? 'fill-rose-500 text-rose-500' : ''} />
                {likeCount}
              </button>
            )
          )}
          <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-150 ml-auto">
            <Share2 size={13} />
            Share
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border shrink-0 px-4">
        {TABS.map((tab) => (
          <button
            key={`detail-tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-3 text-xs font-semibold border-b-2 transition-colors duration-150 ${
              activeTab === tab.id
                ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold tabular-nums ${
                activeTab === tab.id ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {activeTab === 'overview' && (
          <div className="p-5 space-y-5">
            {/* Problem Image */}
            {problem.image && (
              <div className="rounded-xl overflow-hidden h-44 bg-muted">
                <img
                  src={problem.image}
                  alt={problem.imageAlt || problem.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Location */}
            {(problem.town || problem.country || problem.continent) && (
              <div className="flex items-start gap-2 bg-muted/40 rounded-xl p-3">
                <MapPin size={14} className="text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">Location</p>
                  <p className="text-sm text-foreground font-medium">
                    {[problem.town, problem.country, problem.continent].filter(Boolean).join(', ')}
                  </p>
                </div>
              </div>
            )}

            {/* Dates */}
            {(problem.dateOccurred || problem.dateFuture) && (
              <div className="grid grid-cols-2 gap-3">
                {problem.dateOccurred && (
                  <div className="bg-muted/40 rounded-xl p-3">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Date Occurred</p>
                    <p className="text-sm text-foreground font-medium">{problem.dateOccurred}</p>
                  </div>
                )}
                {problem.dateFuture && (
                  <div className="bg-muted/40 rounded-xl p-3">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Expected Future Impact</p>
                    <p className="text-sm text-foreground font-medium">{problem.dateFuture}</p>
                  </div>
                )}
              </div>
            )}

            {/* Problem Description */}
            <div>
              <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">Problem Description</p>
              <div className="prose prose-sm max-w-none text-muted-foreground text-sm leading-relaxed">
                {problem.fullDescription.split('\n\n').map((para, i) => (
                  <p key={`para-${problem.id}-${i}`} className="mb-3 last:mb-0">
                    {para}
                  </p>
                ))}
              </div>
            </div>

            {/* Affected Group */}
            {problem.affectedGroup && (
              <div>
                <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">Who Is Affected</p>
                <p className="text-sm text-muted-foreground leading-relaxed bg-muted/40 rounded-xl p-3">{problem.affectedGroup}</p>
              </div>
            )}

            {/* Effects */}
            {problem.effects && (
              <div>
                <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">Effects &amp; Impact</p>
                <p className="text-sm text-muted-foreground leading-relaxed bg-muted/40 rounded-xl p-3">{problem.effects}</p>
              </div>
            )}

            {/* Workaround */}
            {problem.workaround && (
              <div>
                <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">Current Workaround</p>
                <p className="text-sm text-muted-foreground leading-relaxed bg-muted/40 rounded-xl p-3">{problem.workaround}</p>
              </div>
            )}

            {/* Proposed Solution */}
            {problem.proposedSolution && (
              <div>
                <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">Proposed Solution</p>
                <p className="text-sm text-muted-foreground leading-relaxed bg-muted/40 rounded-xl p-3">{problem.proposedSolution}</p>
              </div>
            )}

            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Solutions', value: problem.solutions.length, icon: Lightbulb, color: 'text-violet-500' },
                { label: 'Comments', value: problem.comments.length, icon: MessageSquare, color: 'text-primary' },
                { label: 'Likes', value: likeCount, icon: Heart, color: 'text-rose-500' },
              ].map((stat) => (
                <div key={`overview-stat-${stat.label}`} className="bg-muted/50 rounded-xl p-3 text-center">
                  <stat.icon size={16} className={`${stat.color} mx-auto mb-1`} />
                  <p className="text-lg font-bold text-foreground tabular-nums">{stat.value}</p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Quick CTA */}
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
              <p className="text-sm font-semibold text-foreground mb-1">Have a solution in mind?</p>
              <p className="text-xs text-muted-foreground mb-3">
                Entrepreneurs can propose a solution directly on this problem. Sign up or log in to submit your pitch.
              </p>
              <button
                onClick={onProposeSolution}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground text-xs font-semibold rounded-lg hover:bg-cyan-600 active:scale-95 transition-all duration-150"
              >
                <TrendingUp size={12} /> Propose a Solution
              </button>
            </div>
          </div>
        )}

        {activeTab === 'solutions' && (
          <div className="p-5 space-y-4">
            {problem.solutions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                  <Lightbulb size={20} className="text-muted-foreground" />
                </div>
                <p className="text-sm font-semibold text-foreground">No solutions proposed yet</p>
                <p className="text-xs text-muted-foreground text-center max-w-xs">
                  Be the first entrepreneur to propose a solution to this problem.
                </p>
                <button
                  onClick={onProposeSolution}
                  className="text-xs font-semibold text-primary hover:text-cyan-600 transition-colors"
                >
                  Propose a Solution →
                </button>
              </div>
            ) : (
              problem.solutions.map((sol) => (
                <SolutionCard
                  key={sol.id}
                  solution={sol}
                  currentUserId={currentUserId}
                  onFundClick={() => {
                    if (!currentUserId) {
                      router.push('/sign-up-login-screen');
                      return;
                    }
                    setShowFundingForm(true);
                  }}
                  onUpdate={(updated) => onUpdateSolution(problem.id, updated)}
                  onDelete={(solutionId) => onDeleteSolution(problem.id, solutionId)}
                />
              ))
            )}
          </div>
        )}

        {activeTab === 'comments' && (
          <CommentSection
            problemId={problem.id}
          />
        )}

        {activeTab === 'funding' && (
          <div className="p-5 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-foreground uppercase tracking-wider">All Funding Offers</p>
              <button
                onClick={() => {
                  if (!currentUserId) {
                    router.push('/sign-up-login-screen');
                    return;
                  }
                  setShowFundingForm(true);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-semibold rounded-lg hover:bg-cyan-600 active:scale-95 transition-all duration-150"
              >
                <DollarSign size={11} /> Submit Offer
              </button>
            </div>

            {problem.solutions.flatMap((s) =>
              s.fundingOffers.map((offer) => (
                <div key={offer.id} className="bg-card border border-border rounded-xl overflow-hidden space-y-0">
                  {/* Solution image for this funded deal */}
                  {s.image && (
                    <div className="h-32 bg-muted overflow-hidden">
                      <img
                        src={s.image}
                        alt={s.imageAlt || s.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="p-4 space-y-3">
                    {/* Solution label */}
                    <p className="text-xs font-semibold text-primary truncate">↳ {s.title}</p>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-xs font-bold text-primary">{offer.investor.charAt(0)}</span>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-foreground">{offer.investor}</p>
                          <div className="flex items-center gap-1">
                            <StatusBadge variant="investor" size="sm" />
                            <span className="text-xs text-success font-semibold">⭐ {offer.investorReputation}</span>
                          </div>
                        </div>
                      </div>
                      <StatusBadge variant={offer.status} />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-muted/50 rounded-lg p-2.5">
                        <p className="text-xs text-muted-foreground mb-0.5">Investment Amount</p>
                        <p className="text-sm font-bold text-foreground tabular-nums">${(offer.amount / 1000).toFixed(0)}K</p>
                      </div>
                      <div className="bg-muted/50 rounded-lg p-2.5">
                        <p className="text-xs text-muted-foreground mb-0.5">Equity Ask</p>
                        <p className="text-sm font-bold text-foreground tabular-nums">{offer.equity}%</p>
                      </div>
                    </div>

                    <div className="bg-muted/30 rounded-lg p-2.5">
                      <p className="text-xs text-muted-foreground mb-0.5">Deal Terms</p>
                      <p className="text-xs text-foreground font-medium leading-relaxed">{offer.terms}</p>
                    </div>

                    <p className="text-xs text-muted-foreground">Submitted {offer.submittedAt}</p>
                  </div>
                </div>
              ))
            )}

            {problem.solutions.flatMap((s) => s.fundingOffers).length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                  <DollarSign size={20} className="text-muted-foreground" />
                </div>
                <p className="text-sm font-semibold text-foreground">No funding offers yet</p>
                <p className="text-xs text-muted-foreground text-center max-w-xs">
                  Investors can submit funding offers on solutions proposed for this problem.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Funding Offer Modal */}
      {showFundingForm && (
        <FundingOfferForm
          problemTitle={problem.title}
          solutions={problem.solutions}
          onClose={() => setShowFundingForm(false)}
        />
      )}
    </div>
  );
}